<template>
  <v-card flat color="transparent">
    <v-col cols="12">
      <h2
        :style="`color: ${$vuetify.theme.themes.light.primary}`"
        class="pl-2 pa-sm-0 mb-3"
      >
        {{ $translate('CONFIRM_YOUR_CHOICES') }}
      </h2>

      <p class="pl-2 pa-sm-0">
        {{ $translate('CONFIRM_YOUR_CHOICES_EXPLANATION') }}
      </p>

      <v-alert type="info">{{ $translate('BOOKING_CREDIT') }}</v-alert>

      <BookingProductList />

      <!-- coupon code and total-->
      <v-row align="end" class="ma-0">
        <!-- Coupon code -->
        <v-col class="px-0" cols="12" md="8" lg="8">
          <v-card outlined elevation="0">
            <v-row class="ma-0 no-wrap justify-space-between">
              <v-col cols="6" sm="8" md="9" lg="10">
                <v-text-field
                  @input="onCouponCodeChange"
                  @keydown.enter="applyCouponCode"
                  :label="$translate('ORDER_COUPON_CODE')"
                  :error="couponError"
                  :error-messages="couponErrorMessages"
                  clearable
                  hide-details="auto"
                  v-model="couponCode"
                />
              </v-col>
              <v-col
                cols="6"
                sm="4"
                md="3"
                lg="2"
                align-self="center"
                align="center"
              >
                <v-btn
                  color="primary"
                  :loading="applyingCouponCode"
                  :disabled="couponCode == null || couponCode.trim() === ''"
                  @click="applyCouponCode"
                >
                  <h2>+</h2>
                </v-btn>
              </v-col>
            </v-row>
          </v-card>
        </v-col>

        <!-- Total price -->
        <v-col cols="12" md="4">
          <v-row
            v-if="isProfUser"
            justify="space-between"
            class="ma-0 text-subtitle-2"
          >
            <div>{{ $translate('SUBTOTAL') }}</div>
            <div>{{ formatter.format(subtotalPrice) }}</div>
          </v-row>
          <v-row
            v-if="getTotalDiscount < 0"
            justify="space-between"
            class="ma-0 text-subtitle-2"
          >
            <div>{{ $translate('DISCOUNT') }}</div>
            <div class="red--text">
              {{ formatter.format(getTotalDiscount) }}
            </div>
          </v-row>
          <v-row
            v-if="isProfUser"
            justify="space-between"
            class="ma-0 text-subtitle-2"
          >
            <div>{{ $translate('VAT') }}</div>
            <div>{{ formatter.format(getTotalVatPrice) }}</div>
          </v-row>
          <v-row
            v-if="getDepositValue > 0"
            justify="space-between"
            class="ma-0 text-subtitle-2"
          >
            <div>{{ $translate('DEPOSIT_VALUE') }}</div>
            <div>{{ formatter.format(getDepositValue) }}</div>
          </v-row>
          <v-row
            justify="space-between"
            class="ma-0 mt-1 text-h6 font-weight-bold"
          >
            <div>{{ $translate('LINDA_TOTAL_PRICE') }}</div>
            <div>{{ formatter.format(getTotalPrice) }}</div>
          </v-row>
        </v-col>
      </v-row>

      <!-- growthVitaminCards -->
      <v-row class="ma-0">
        <v-col cols="12" class="pa-0">
          <template v-for="(growthVitamin, gvIndex) in growthVitamins">
            <v-col :key="gvIndex" class="px-0 py-1">
              <v-card
                v-if="growthVitamin.DiscountInfo.Type == 'Free Delivery'"
                class="primary-font-color"
                color="primary"
                flat
              >
                <v-row class="ma-0" align="center">
                  <v-avatar size="32" tile class="mr-3">
                    <v-icon color="white">mdi-truck</v-icon>
                  </v-avatar>
                  <v-col class="px-3 py-1">
                    <div class="text-subtitle-2">
                      {{ $translate('FREE_DELIVERY') }}
                    </div>
                    <div
                      class="text-caption"
                      v-html="growthVitamin.Description"
                    />
                  </v-col>
                </v-row>
              </v-card>
              <v-card v-else class="primary-font-color" color="primary" flat>
                <v-row class="ma-0" align="center">
                  <v-avatar size="32" tile class="mx-3">
                    <v-icon color="white">mdi-lightbulb-on</v-icon>
                  </v-avatar>
                  <v-col class="px-3 py-1">
                    <div class="text-subtitle-2">{{ growthVitamin.Name }}</div>
                    <div
                      class="text-caption"
                      v-html="growthVitamin.Description"
                    />
                    <div class="text-caption">
                      {{ getGrowthVitaminInfo(growthVitamin) }}
                    </div>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </template>
        </v-col>
      </v-row>
      <v-row>
     <v-spacer />
     <v-btn color="secondary" class="text-capitalize mx-5" @click="back()">
          {{ $translate('PRODUCTS_ADD_MORE') }}
        </v-btn>

        <v-btn
          :disabled="emptyShoppingCart"
          color="primary"
          class="text-capitalize primary-font-color"
          @click="onCompleteBtnClick()"
        >
          {{ $translate('BOOKING_COMPLETE') }}
        </v-btn>
      </v-row>

      <!-- order suggestions -->
      <div v-if="showSuggestions" class="mt-6">
        <h2>{{ $translate('BOOKING_OUR_SUGGESTIONS') }}</h2>
        <Suggestions :suggestions="bookingSuggestions"></Suggestions>
      </div>


      <!-- General confirm dialog -->
      <ConfirmDialog
        :visible="showConfirmDialog"
        :title="confirmDialogTitle"
        :message="confirmDialogMsg"
        :positiveBtnText="confirmDialogPositiveBtnText"
        :negativeBtnText="confirmDialogNegativeBtnText"
        @positiveBtnClicked="addCouponCodeFromDialog"
        @negativeBtnClicked="ignoreCouponCode"
      ></ConfirmDialog>
    </v-col>
  </v-card>
</template>

<script>
import doRequest from '@/api/dssRequest';
import { mapGetters } from 'vuex';
import ConfirmDialog from '@/components/generalComponents/uiComponents/ConfirmDialog';
import BookingProductList from './Booking_Product_List';
import Suggestions from '@/components/generalComponents/Suggestions';
import ShoppingCart from '@/objects/ShoppingCart';
import utility from '@/shared/utility';

export default {
  name: 'BookingOrder',
  props: {
    eventDetails: {
      type: Object,
      required: true,
      default: () => ({}),
    },
  },
  components: {
    Suggestions,
    ConfirmDialog,
    BookingProductList,
  },
  data() {
    return {
      productShoppingCart: [],
      formatter: new Intl.NumberFormat('nl', {
        style: 'currency',
        currency: 'EUR',
      }),
      shoppingInfoDialogText: '',
      shoppingCartInfoDialog: false,
      deliveryFees: '',
      allOrders: [],
      emptyShoppingCart: false,
      applyingCouponCode: false,
      // suggestions
      showSuggestions: false,
      bookingSuggestions: [],

      // Coupon code
      couponCode: '',
      couponError: false,
      couponErrorMessages: [],
      couponCodeApplied: false,

      // Confirm dialog
      showConfirmDialog: false,
      confirmDialogTitle: '',
      confirmDialogMsg: '',
      confirmDialogPositiveBtnText: '',
      confirmDialogNegativeBtnText: '',
    };
  },
  computed: {
    ...mapGetters({
      GVTimer: 'getGVTimer',
      isProfUser: 'isProfUser',
    }),
    ordersInCart() {
      return this.$store.getters['shoppingCart/orders'];
    },
    getTotalDiscount() {
      return ShoppingCart.getTotalDiscount(this.allOrders);
    },
    getTotalPrice() {
      return ShoppingCart.getTotalPrice(this.allOrders);
    },
    getTotalVatPrice() {
      return ShoppingCart.getTotalVatPrice(this.allOrders);
    },
    subtotalPrice() {
      return ShoppingCart.getSubtotalPrice(this.allOrders);
    },
    getDepositValue() {
      return ShoppingCart.getDepositValue(this.allOrders);
    },
    applyCouponCodeRequired() {
      // Check if the coupon code needs to be applied before contining to next step
      if (
        this.couponCode != null &&
        this.couponCode.trim() !== '' &&
        !this.couponCodeApplied
      ) {
        // Coupon code filled in but not applied yet
        return true;
      }
      return false;
    },
    userObjectId() {
      return this.$store.getters['auth/userName']
        ? this.$store.getters['auth/userName']
        : this.$store.getters['auth/tmpUserId'];
    },
    growthVitamins() {
      let gvList = [];
      const allGrowthVitamins = this.$store.getters['getGrowthVitamins'];
      if (allGrowthVitamins == null) {
        return gvList;
      }
      allGrowthVitamins.forEach((gv) => {
        if (gv.DiscountInfo != null && gv.DiscountInfo.Type !== 'Tip') {
          gvList.push(gv);
        }
      });
      return gvList;
    },
    userLoggedIn() {
      return this.$store.getters['auth/isValid'];
    },
  },
  watch: {
    ordersInCart() {
      this.getAwardedGrowthVitaminCards();
    },
  },
  mounted() {
    // filter array items based on ShoppingCartType === 'Product' and put it in an array
    this.productShoppingCart = this.$store.getters[
      'shoppingCart/orders'
    ].filter((item) => item.ShoppingCartType === 'Product');

    this.getAwardedGrowthVitaminCards();
  },
  methods: {
    uppercase() {
      this.couponCode = this.couponCode.toUpperCase();
    },
    applyCouponCode() {
      // variable to add orders to payload
      const allEventsForOrder = {};
      // loop through ticket/rental orders and set them in the variable
      this.ordersInCart.forEach((orderItem) => {
        // to create index in array with that EventId
        if (allEventsForOrder[orderItem.EventId] == null) {
          allEventsForOrder[orderItem.EventId] = [];
        }
        // add the items to that index
        allEventsForOrder[orderItem.EventId].push(orderItem.OrderId);
      });
      // payload should include a coupon code and a list of reservations
      const payload = {
        CouponCode: this.couponCode,
        Reservations: allEventsForOrder,
      };

      this.couponCodeApplied = false;
      this.applyingCouponCode = true;

      doRequest(
        (response) => {
          this.applyingCouponCode = false;
          this.couponCodeApplied = true;
          this.couponError = false;
          this.couponErrorMessages = [];
          this.couponCode = '';
          this.$store.commit(
            'setGrowthVitamins',
            response.data.GrowthVitaminCards
              ? response.data.GrowthVitaminCards
              : {}
          );
          // set the updated orders in the orders store variable
          this.allOrders = response.data.Orders;
        },
        (error) => {
          this.onApplyCouponCodeError(error);
        },
        'applyCouponCode',
        this.userObjectId,
        payload
      );
    },
    onApplyCouponCodeError(error) {
      this.applyingCouponCode = false;
      this.couponError = true;
      switch (error.data.ErrorCode) {
        case 'E_GV_001':
        case 'E_GV_002':
        case 'E_GV_004':
        case 'E_GV_005':
          this.couponErrorMessages = [this.$translate(error.data.ErrorCode)];
          break;
        default:
          this.couponErrorMessages = [this.$translate('E_GV_002')];
          break;
      }
    },
    getAwardedGrowthVitaminCards() {
      // variable to add orders to payload
      const allEventsForOrder = {};
      // loop through ticket/rental orders and set them in the variable
      this.ordersInCart.forEach((orderItem) => {
        // to create index in array with that EventId
        if (allEventsForOrder[orderItem.EventId] == null) {
          allEventsForOrder[orderItem.EventId] = [];
        }
        // add the items to that index
        allEventsForOrder[orderItem.EventId].push(orderItem.OrderId);
      });

      const payload = {
        Reservations: allEventsForOrder || {},
      };

      doRequest(
        (response) => {
          this.$store.commit(
            'setGrowthVitamins',
            response.data.GrowthVitaminCards
              ? response.data.GrowthVitaminCards
              : {}
          );

          // set the updated orders in the orders store variable
          this.allOrders = response.data.Orders;

          // check for suggestions
          if (response.data.Suggestions.length !== 0) {
            // save the suggestions
            this.bookingSuggestions = response.data.Suggestions;
            // set suggestions to visible
            this.showSuggestions = true;
          }
        },
        () => {},
        'getAwardedGrowthVitaminCards',
        this.userObjectId,
        payload
      );
    },
    openInfoDialogFromShoppingCart(type) {
      switch (type) {
        case 'Product':
          this.shoppingInfoDialogText = this.$translate('PRODUCT_INFO_TEXT');
          this.shoppingCartInfoDialog = true;
          break;
        case 'Request':
          this.shoppingInfoDialogText = this.$translate('REQUEST_INFO_TEXT');
          this.shoppingCartInfoDialog = true;
          break;
        default:
      }
    },
    back() {
      this.$emit('back');
    },
    roundPrice(price) {
      return Number(price).toFixed(2).replace('.', ',');
    },
    onCouponCodeChange() {
      // Clear errors
      this.couponErrorMessages = [];
      this.couponError = false;

      this.couponCode = this.couponCode.toUpperCase();
      this.couponCodeApplied = false;
    },
    onCompleteBtnClick() {
      // Check if user has to apply coupon code before continuing to next step
      if (this.applyCouponCodeRequired) {
        this.showCouponReminderDialog();
      } else {
        this.$emit('continue', this.allOrders);
      }
    },
    showCouponReminderDialog() {
      this.confirmDialogTitle = this.$translate('COUPON_NOT_APPLIED_TITLE');
      this.confirmDialogMsg = this.$translate('COUPON_NOT_APPLIED_MESSAGE');
      this.confirmDialogPositiveBtnText = this.$translate('YES');
      this.confirmDialogNegativeBtnText = this.$translate('NO');
      this.showConfirmDialog = true;
    },
    addCouponCodeFromDialog() {
      // User wants to add the coupon code before continuing
      this.applyCouponCode();
      this.showConfirmDialog = false;
    },
    ignoreCouponCode() {
      // User wants to continue without adding the coupon code
      this.couponCode = '';
      this.showConfirmDialog = false;
      this.$emit('continue');
    },
    getGrowthVitaminExpireTimeText(growthVitamin) {
      if (
        growthVitamin.DiscountInfo.Type === 'Tip' ||
        growthVitamin.ExpiryUnixTime == null ||
        growthVitamin.ExpiryUnixTime <= 0
      ) {
        return '';
      }

      const expireTimeStr = utility.unixTimeToDateTimeString(
        growthVitamin.ExpiryUnixTime
      );
      const result = utility.sprintf(
        this.$translate('GROWTH_VITAMIN_EXPIRE_TEXT'),
        expireTimeStr
      );

      return result;
    },
    getGrowthMinimumOrderValueText(growthVitamin) {
      if (
        growthVitamin.DiscountInfo.Type === 'Tip' ||
        growthVitamin.DiscountInfo.MinimumOrderValue == null ||
        growthVitamin.DiscountInfo.MinimumOrderValue <= 0
      ) {
        return '';
      }

      const result = utility.sprintf(
        this.$translate('GROWTH_VITAMIN_MIN_VALUE_TEXT'),
        growthVitamin.DiscountInfo.MinimumOrderValue
      );

      return result;
    },
    getGrowthVitaminInfo(growthVitamin) {
      var resultText = '';

      let expireTimeText = this.getGrowthVitaminExpireTimeText(growthVitamin);
      if (expireTimeText !== '') {
        resultText = expireTimeText + '. ';
      }

      let minOrderValueText =
        this.getGrowthMinimumOrderValueText(growthVitamin);
      if (minOrderValueText !== '') {
        resultText += minOrderValueText + '.';
      }

      return resultText;
    },
  },
};
</script>

<style scoped>
.subtotal {
  color: black !important;
  text-align: right;
}
.adressStyling {
  margin-bottom: 4px;
}
.coupon-code input {
  text-transform: uppercase;
}
</style>
